import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, User, Shield, CreditCard, Save, Loader2, Lock,
  Eye, EyeOff, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "security", label: "Security", icon: Shield },
  { id: "subscription", label: "Subscription", icon: CreditCard },
];

const ROLES = [
  { value: "ops", label: "Operations" },
  { value: "quality", label: "Quality" },
  { value: "pm", label: "Project Management" },
  { value: "consultant", label: "Consultant" },
  { value: "analyst", label: "Data Analyst" },
  { value: "executive", label: "Executive / Leadership" },
];

const EXPERIENCE_LEVELS = [
  { value: "white_belt", label: "White Belt" },
  { value: "yellow_belt", label: "Yellow Belt" },
  { value: "green_belt", label: "Green Belt" },
  { value: "black_belt", label: "Black Belt" },
  { value: "master_black_belt", label: "Master Black Belt" },
];

const INDUSTRIES = [
  "Healthcare", "Manufacturing", "Finance", "Technology", "Retail",
  "Logistics", "Pharmaceuticals", "Aerospace", "Automotive", "Other",
];

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function timeAgo(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

function SectionCard({ id, title, description, children }) {
  return (
    <div id={id} className="bg-card border border-border rounded-xl p-6 scroll-mt-24" data-testid={`settings-section-${id}`}>
      <div className="mb-5">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function FieldRow({ label, helper, children, htmlFor }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 md:gap-6 items-start">
      <div className="pt-2">
        <Label htmlFor={htmlFor} className="text-sm font-medium">{label}</Label>
        {helper && <p className="text-xs text-muted-foreground mt-0.5">{helper}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SelectField({ value, onValueChange, placeholder, options, id }) {
  return (
    <Select value={value || ""} onValueChange={onValueChange}>
      <SelectTrigger id={id} className="w-full" data-testid={`select-${id}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ProfileSettings() {
  const { user, token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [subSummary, setSubSummary] = useState(null);
  const [activeSection, setActiveSection] = useState("profile");
  const [saving, setSaving] = useState({});

  const fetchData = useCallback(async () => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`${API}/account/profile`, { headers: h }),
        fetch(`${API}/account/subscription-summary`, { headers: h }),
      ]);
      if (pRes.ok) setProfile(await pRes.json());
      if (sRes.ok) setSubSummary(await sRes.json());
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0.1 }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading]);

  const set = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));

  const saveProfile = async () => {
    setSaving(s => ({ ...s, profile: true }));
    try {
      const res = await fetch(`${API}/account/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: profile.name,
          role: profile.role,
          industry: profile.industry,
          experience_level: profile.experience_level,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Save failed");
      const updated = await res.json();
      setProfile(updated);
      toast.success("Profile saved");
      checkAuth();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(s => ({ ...s, profile: false }));
    }
  };

  // Password
  const [pwForm, setPwForm] = useState({ current: "", new_pw: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, new_pw: false, confirm: false });
  const [pwError, setPwError] = useState("");

  const changePassword = async () => {
    setPwError("");
    if (pwForm.new_pw.length < 8) { setPwError("New password must be at least 8 characters"); return; }
    if (pwForm.new_pw !== pwForm.confirm) { setPwError("Passwords do not match"); return; }
    setSaving(s => ({ ...s, password: true }));
    try {
      const res = await fetch(`${API}/account/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.new_pw }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Password change failed");
      toast.success("Password updated");
      setPwForm({ current: "", new_pw: "", confirm: "" });
      fetchData();
    } catch (e) {
      setPwError(e.message);
    } finally {
      setSaving(s => ({ ...s, password: false }));
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isEmailAuth = profile.auth_provider === "email" || !!profile.password_updated_at;

  return (
    <div className="min-h-screen bg-background" data-testid="profile-settings-page">
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="settings-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-sm font-bold text-primary">
                {initials(profile.name)}
              </div>
              <div>
                <h1 className="text-base font-semibold leading-tight">Profile & Settings</h1>
                <p className="text-xs text-muted-foreground">Manage your account and security</p>
              </div>
            </div>
          </div>
          {profile.profile_updated_at && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" /> Updated {timeAgo(profile.profile_updated_at)}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex gap-8">
        <nav className="hidden lg:block w-44 shrink-0 sticky top-24 self-start" data-testid="settings-sidebar">
          <ul className="space-y-0.5">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === s.id
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    }`}
                    data-testid={`nav-${s.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex-1 min-w-0 space-y-6">

          {/* ═══ Profile ═══ */}
          <SectionCard id="profile" title="Profile Information" description="Fields used across the platform and for AI coaching personalization">
            <div className="space-y-5">
              <div className="flex items-center gap-4 pb-4 border-b border-border/50">
                <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center text-xl font-bold text-primary shrink-0">
                  {initials(profile.name)}
                </div>
                <div>
                  <p className="font-medium">{profile.name}</p>
                  <p className="text-xs text-muted-foreground">{profile.email}</p>
                  {profile.auth_provider && profile.auth_provider !== "email" && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      Signed in via {profile.auth_provider.charAt(0).toUpperCase() + profile.auth_provider.slice(1)}
                    </span>
                  )}
                </div>
              </div>

              <FieldRow label="Full Name" htmlFor="name">
                <Input id="name" value={profile.name || ""} onChange={e => set("name", e.target.value)} placeholder="Your name" data-testid="input-name" />
              </FieldRow>
              <FieldRow label="Email" helper="Email cannot be changed">
                <Input value={profile.email || ""} disabled className="opacity-60" data-testid="input-email" />
              </FieldRow>
              <FieldRow label="Functional Role" htmlFor="role">
                <SelectField id="role" value={profile.role} onValueChange={v => set("role", v)} placeholder="Select role" options={ROLES} />
              </FieldRow>
              <FieldRow label="Industry" htmlFor="industry">
                <Select value={profile.industry || ""} onValueChange={v => set("industry", v)}>
                  <SelectTrigger id="industry" className="w-full" data-testid="select-industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Experience Level" htmlFor="experience_level" helper="Affects AI coaching depth — White Belt gets step-by-step guidance, Black Belt gets advanced analysis">
                <SelectField id="experience_level" value={profile.experience_level} onValueChange={v => set("experience_level", v)} placeholder="Select level" options={EXPERIENCE_LEVELS} />
              </FieldRow>
            </div>
            <div className="flex justify-end mt-6 pt-4 border-t border-border/50">
              <Button onClick={saveProfile} disabled={saving.profile} data-testid="save-profile-btn">
                {saving.profile ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Profile
              </Button>
            </div>
          </SectionCard>

          {/* ═══ Security ═══ */}
          <SectionCard id="security" title="Security" description="Manage your authentication and password">
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm font-medium">Authentication Method</p>
                  <p className="text-xs text-muted-foreground">
                    {isEmailAuth ? "Email & Password" : `${profile.auth_provider?.charAt(0).toUpperCase()}${profile.auth_provider?.slice(1)} Account`}
                  </p>
                </div>
              </div>
              {profile.password_updated_at && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Password last changed {timeAgo(profile.password_updated_at)}
                </p>
              )}

              {isEmailAuth ? (
                <div className="space-y-4 pt-2">
                  <FieldRow label="Current Password" htmlFor="pw-current">
                    <div className="relative">
                      <Input id="pw-current" type={showPw.current ? "text" : "password"} value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} placeholder="Enter current password" data-testid="input-current-password" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(s => ({ ...s, current: !s.current }))}>
                        {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FieldRow>
                  <FieldRow label="New Password" htmlFor="pw-new" helper="Minimum 8 characters">
                    <div className="relative">
                      <Input id="pw-new" type={showPw.new_pw ? "text" : "password"} value={pwForm.new_pw} onChange={e => setPwForm(f => ({ ...f, new_pw: e.target.value }))} placeholder="Enter new password" data-testid="input-new-password" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(s => ({ ...s, new_pw: !s.new_pw }))}>
                        {showPw.new_pw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FieldRow>
                  <FieldRow label="Confirm Password" htmlFor="pw-confirm">
                    <div className="relative">
                      <Input id="pw-confirm" type={showPw.confirm ? "text" : "password"} value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Confirm new password" data-testid="input-confirm-password" />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(s => ({ ...s, confirm: !s.confirm }))}>
                        {showPw.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FieldRow>
                  {pwError && <p className="text-sm text-destructive" data-testid="pw-error">{pwError}</p>}
                  <div className="flex justify-end pt-2">
                    <Button onClick={changePassword} disabled={saving.password || !pwForm.current || !pwForm.new_pw || !pwForm.confirm} variant="outline" data-testid="change-password-btn">
                      {saving.password ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                      Change Password
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  Your account uses {profile.auth_provider} authentication. Password management is handled by your identity provider.
                </p>
              )}
            </div>
          </SectionCard>

          {/* ═══ Subscription ═══ */}
          <SectionCard id="subscription" title="Subscription & Credits" description="Your current plan and credit usage">
            {subSummary ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-muted/30" data-testid="sub-plan">
                    <p className="text-xs text-muted-foreground">Current Plan</p>
                    <p className="text-lg font-bold capitalize">{subSummary.plan_name}</p>
                    {subSummary.price_monthly > 0 && <p className="text-xs text-muted-foreground">${subSummary.price_monthly}/mo</p>}
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30" data-testid="sub-monthly-credits">
                    <p className="text-xs text-muted-foreground">Monthly Credits</p>
                    <p className="text-lg font-bold">{subSummary.monthly_credits_remaining} <span className="text-sm font-normal text-muted-foreground">/ {subSummary.monthly_credits_total}</span></p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30" data-testid="sub-purchased-credits">
                    <p className="text-xs text-muted-foreground">Purchased Credits</p>
                    <p className="text-lg font-bold">{subSummary.purchased_credits_remaining}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30" data-testid="sub-member-since">
                    <p className="text-xs text-muted-foreground">Member Since</p>
                    <p className="text-sm font-medium">{subSummary.member_since ? new Date(subSummary.member_since).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}</p>
                  </div>
                </div>
                {subSummary.has_pending_downgrade && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400">
                    Your plan will downgrade to <span className="font-medium capitalize">{subSummary.pending_downgrade_to}</span> at the end of the current billing cycle.
                  </div>
                )}
                {subSummary.credits_reset_date && (
                  <p className="text-xs text-muted-foreground">Monthly credits reset on {new Date(subSummary.credits_reset_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                )}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" size="sm" onClick={() => navigate("/pricing")} data-testid="manage-plan-btn">Manage Plan</Button>
                  <Button variant="outline" size="sm" onClick={() => navigate("/buy-credits")} data-testid="buy-credits-btn">Buy Credits</Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4">Unable to load subscription details.</p>
            )}
          </SectionCard>

        </div>
      </div>
    </div>
  );
}
